import { createContext, PropsWithChildren, useContext, useMemo } from 'react';

import type { CoursePurchase } from 'shared/common/dtos/course/coursePurchase';
import { GetUserSubscriptionDataResponseType } from 'shared/common/dtos/course/getUserSubscriptionData';

type CourseContextData = {
  courseActivityId?: string;
  courseCategoryId?: string;
  courseDraftId?: string;
  courseEnvironmentId?: string;
  coursePurchase?: CoursePurchase;
  courseTitle?: string;
  courseUrlName?: string;
  userSubscriptionData?: GetUserSubscriptionDataResponseType;
};

const CourseContext = createContext<CourseContextData>({
  courseActivityId: undefined,
  courseCategoryId: undefined,
  courseDraftId: undefined,
  courseEnvironmentId: undefined,
  coursePurchase: undefined,
  courseTitle: undefined,
  courseUrlName: undefined,
  userSubscriptionData: undefined
});

export function useCourseContextData() {
  const context = useContext(CourseContext);
  return context;
}

type CourseContextProviderProps = PropsWithChildren<CourseContextData>;

export function CourseContextProvider(props: CourseContextProviderProps) {
  const {
    courseActivityId,
    courseCategoryId,
    courseDraftId,
    courseEnvironmentId,
    coursePurchase,
    courseUrlName,
    courseTitle,
    children,
    userSubscriptionData
  } = props;

  const value = useMemo(
    () => ({
      courseActivityId,
      courseCategoryId,
      courseDraftId,
      courseEnvironmentId,
      coursePurchase,
      courseTitle,
      courseUrlName,
      userSubscriptionData
    }),
    [
      courseActivityId,
      courseCategoryId,
      courseDraftId,
      courseEnvironmentId,
      coursePurchase,
      courseTitle,
      courseUrlName,
      userSubscriptionData
    ]
  );

  return <CourseContext.Provider value={value}>{children}</CourseContext.Provider>;
}
